/**
 * P8 Fase B — Onboarding Invite Tokens admin.
 * Route: /admin/onboarding/tokens
 *
 * Generate single-use or multi-use registration links with optional auto-email
 * delivery to the invitee. Built for the CRM team to attract new partners,
 * suppliers, investors and clients in a controlled, auditable way.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, Plus, Link2, Loader2, Copy, Trash2, Mail, ShieldCheck,
  Clock, CheckCircle2, XCircle, AlertCircle, Search,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  active:    'bg-emerald-100 text-emerald-800',
  expired:   'bg-amber-100 text-amber-800',
  revoked:   'bg-rose-100 text-rose-800',
  exhausted: 'bg-slate-200 text-slate-700',
};

const ROLE_OPTIONS = [
  { v: '',         l: 'Any (no preset)' },
  { v: 'client',   l: 'Client' },
  { v: 'supplier', l: 'Supplier' },
  { v: 'partner',  l: 'Partner' },
  { v: 'investor', l: 'Investor' },
  { v: 'other',    l: 'Other' },
];


const OnboardingTokens = () => {
  const { toast } = useToast();
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  // Form state
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(30);
  const [maxUses, setMaxUses] = useState(1);
  const [suggestedRole, setSuggestedRole] = useState('__any__');
  const [targetEmail, setTargetEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/onboarding/admin/tokens`, {
        ...auth, params: { status: statusFilter || undefined, limit: 200 },
      });
      setList(r.data || []);
    } catch (e) {
      toast({ title: 'Could not load tokens', description: e?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [auth, statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return list;
    return list.filter(t =>
      (t.label || '').toLowerCase().includes(q) ||
      (t.target_email || '').toLowerCase().includes(q) ||
      (t.token || '').toLowerCase().includes(q),
    );
  }, [list, search]);

  const createToken = useCallback(async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API_URL}/api/onboarding/admin/tokens`, {
        label: label || null,
        expires_in_days: Number(days),
        max_uses: Number(maxUses),
        suggested_role: suggestedRole && suggestedRole !== '__any__' ? suggestedRole : null,
        target_email: targetEmail || null,
      }, auth);
      setCreatedToken(r.data);
      toast({
        title: 'Invite link generated',
        description: targetEmail ? `Email queued to ${targetEmail}` : 'Copy the link below to share.',
      });
      setLabel(''); setDays(30); setMaxUses(1); setSuggestedRole('__any__'); setTargetEmail('');
      await load();
    } catch (e) {
      toast({ title: 'Failed to create token', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setCreating(false); }
  }, [auth, label, days, maxUses, suggestedRole, targetEmail, toast, load]);

  const revoke = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/api/onboarding/admin/tokens/${id}`, auth);
      toast({ title: 'Token revoked' });
      await load();
    } catch (e) {
      toast({ title: 'Revoke failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  }, [auth, toast, load]);

  const buildLink = (tk) => `${window.location.origin}/register/entity/${tk}`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="onboarding-tokens-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center shadow-lg">
          <Link2 className="w-5 h-5 text-[#C9A86A]" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#2B2D42]">Invite Tokens</h1>
          <p className="text-sm text-[#6C757D]">Generate single-use or multi-use registration links — auditable, expirable and revokable.</p>
        </div>
        <Button onClick={() => { setCreatedToken(null); setCreateOpen(true); }}
          data-testid="token-new-btn"
          className="bg-[#C9A86A] hover:bg-[#B7945A] text-black font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New invite
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        {[
          { id: '',          l: 'All' },
          { id: 'active',    l: 'Active' },
          { id: 'expired',   l: 'Expired' },
          { id: 'exhausted', l: 'Exhausted' },
          { id: 'revoked',   l: 'Revoked' },
        ].map(f => (
          <button key={f.id || 'all'} onClick={() => setStatusFilter(f.id)} data-testid={`token-filter-${f.id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              statusFilter === f.id
                ? 'bg-[#0F172A] text-white border-[#0F172A]'
                : 'bg-white text-[#2B2D42] border-slate-200 hover:border-[#0F172A]'
            }`}>
            {f.l}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 max-w-sm w-full">
          <Search className="w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search label, email or token…"
            className="h-9" data-testid="token-search" />
        </div>
      </div>

      {/* List */}
      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Link2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No invite tokens yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="token-list">
            {filtered.map((t) => {
              const link = buildLink(t.token);
              const isActive = t.status === 'active';
              return (
                <li key={t.id} data-testid={`token-${t.id}`}
                  className="px-5 py-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-[#2B2D42]">{t.label || 'Invitation'}</span>
                      <Badge className={`${STATUS_BADGE[t.status] || ''} text-[10px] uppercase`}>{t.status}</Badge>
                      {t.suggested_role && (
                        <Badge className="bg-violet-100 text-violet-800 text-[10px]">{t.suggested_role.toUpperCase()}</Badge>
                      )}
                      <span className="text-[10px] text-[#6C757D] font-mono">{t.uses}/{t.max_uses} uses</span>
                    </div>
                    <div className="text-xs text-[#6C757D] flex flex-wrap gap-x-3">
                      {t.target_email && <span><Mail className="w-3 h-3 inline mr-1" />{t.target_email}</span>}
                      <span><Clock className="w-3 h-3 inline mr-1" />Expires {(t.expires_at || '').slice(0, 10)}</span>
                      <span className="font-mono text-slate-400">·{t.token.slice(0, 8)}…</span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copy(link)} data-testid={`token-copy-${t.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                  </Button>
                  {isActive && (
                    <Button size="sm" variant="outline" onClick={() => revoke(t.id)}
                      data-testid={`token-revoke-${t.id}`}
                      className="text-rose-600 border-rose-200 hover:bg-rose-50">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent></Card>

      {/* New token dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="token-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C9A86A]" /> Generate invite link
            </DialogTitle>
          </DialogHeader>

          {!createdToken ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="tk-label">Internal label</Label>
                <Input id="tk-label" value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Trade fair Dubai 2026"
                  data-testid="token-label-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="tk-days">Expires in (days)</Label>
                  <Input id="tk-days" type="number" min={1} max={365}
                    value={days} onChange={(e) => setDays(e.target.value)}
                    data-testid="token-days-input" />
                </div>
                <div>
                  <Label htmlFor="tk-uses">Max uses</Label>
                  <Input id="tk-uses" type="number" min={1} max={100}
                    value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                    data-testid="token-uses-input" />
                </div>
              </div>
              <div>
                <Label>Pre-fill role on form (optional)</Label>
                <Select value={suggestedRole} onValueChange={setSuggestedRole}>
                  <SelectTrigger data-testid="token-role-select"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any (no preset)</SelectItem>
                    {ROLE_OPTIONS.filter(o => o.v).map(o => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tk-email">Send link by email to (optional)</Label>
                <Input id="tk-email" type="email" value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  placeholder="prospect@example.com"
                  data-testid="token-email-input" />
                <p className="text-[11px] text-slate-500 mt-1">
                  If empty, you can copy the generated link manually after creation.
                </p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={createToken} disabled={creating} data-testid="token-create-btn"
                  className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Generate</>}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="token-create-result">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <div className="font-semibold">Invite link generated</div>
                  <div className="text-xs text-emerald-700">
                    {createdToken.target_email
                      ? `An email has been queued to ${createdToken.target_email}.`
                      : 'Copy the link below and share it with your invitee.'}
                  </div>
                </div>
              </div>
              <div>
                <Label>Shareable link</Label>
                <div className="flex gap-2">
                  <Input readOnly value={buildLink(createdToken.token)} className="font-mono text-xs"
                    data-testid="token-result-link" />
                  <Button onClick={() => copy(buildLink(createdToken.token))} data-testid="token-result-copy-btn">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-slate-500">
                Expires <b>{(createdToken.expires_at || '').slice(0, 10)}</b> · max
                uses <b>{createdToken.max_uses}</b>.
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setCreatedToken(null); setCreateOpen(false); }}>Close</Button>
                <Button onClick={() => setCreatedToken(null)} className="bg-[#C9A86A] hover:bg-[#B7945A] text-black">
                  <Plus className="w-4 h-4 mr-1" /> Create another
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingTokens;
